import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader, Plus, RefreshCw, ShoppingCart, UserPlus } from 'lucide-react';
import { assignSeller, getAltaManualConfig, listSales, listSellers, type OrgUser, type Sale } from '../api';
import { RegistrarVenta } from './RegistrarVenta';

/** 🔒 El vendedor recibe SOLO sus ventas — lo fuerza el servidor.
 *  El admin ve todas y puede ATRIBUIR una venta a una vendedora (ese es el
 *  eslabón que permite comisionar ventas capturadas a mano). */

const MXN = (v: string | number) =>
  `$${Number(v).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;

const ESTADO: Record<string, string> = {
  paid: 'bg-emerald-500/12 text-emerald-400 border-emerald-500/25',
  pending: 'bg-amber-500/12 text-amber-400 border-amber-500/25',
  cancelled: 'bg-red-500/12 text-red-400 border-red-500/25',
  refunded: 'bg-red-500/12 text-red-400 border-red-500/25',
  expired: 'bg-muted text-muted-foreground border-border',
};

const ESTADO_TXT: Record<string, string> = {
  paid: 'Pagada', pending: 'Pendiente', cancelled: 'Cancelada', refunded: 'Reembolsada', expired: 'Vencida',
};

export function MisVentas({ isAdmin }: { isAdmin: boolean }) {
  const [items, setItems] = useState<Sale[]>([]);
  const [sellers, setSellers] = useState<OrgUser[]>([]);
  const [quien, setQuien] = useState('');
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  const [puedeRegistrar, setPuedeRegistrar] = useState(false);
  const [registrando, setRegistrando] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try { setItems(await listSales(quien || undefined)); }
    catch (e: any) { setErr(e.message); }
    finally { setLoading(false); }
  }, [quien]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    getAltaManualConfig().then(c => setPuedeRegistrar(c.puede_registrar)).catch(() => setPuedeRegistrar(false));
  }, []);
  useEffect(() => {
    if (!isAdmin) return;
    listSellers().then(setSellers).catch(() => setSellers([]));
  }, [isAdmin]);

  const nombreDe = useMemo(() => {
    const m = new Map(sellers.map(s => [s.user_id, s.full_name || s.username]));
    return (id: string | null) => (id ? m.get(id) || id.slice(0, 8) : null);
  }, [sellers]);

  const asignar = async (venta: Sale, sellerId: string) => {
    if (!sellerId) return;
    setBusy(venta.id);
    try { await assignSeller(venta.id, sellerId); await load(); }
    catch (e: any) { setErr(e.message); }
    finally { setBusy(null); }
  };

  const cobrado = items
    .filter(s => s.status === 'paid')
    .reduce((a, s) => a + Number(s.total), 0);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">{isAdmin ? 'Ventas del equipo' : 'Mis ventas'}</h2>
          <p className="text-sm text-muted-foreground">
            {isAdmin
              ? 'Todas las ventas. Asigna una vendedora para que se le comisione.'
              : 'Solo las ventas atribuidas a ti.'}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {puedeRegistrar && (
            <button onClick={() => setRegistrando(true)}
              className="orion-btn-brand flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold">
              <Plus size={15} />Registrar venta
            </button>
          )}
          <button onClick={load} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-muted hover:bg-muted/70">
            <RefreshCw size={13} />Actualizar
          </button>
        </div>
      </div>
      <RegistrarVenta open={registrando} onClose={() => setRegistrando(false)} onCreated={load} isAdmin={isAdmin} />

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <div className="rounded-xl border border-border bg-card p-4">
          <p className="text-[11px] uppercase tracking-widest text-muted-foreground">Ventas</p>
          <p className="text-2xl font-bold mt-1 tabular-nums">{items.length}</p>
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          <p className="text-[11px] uppercase tracking-widest text-muted-foreground">Cobrado</p>
          <p className="text-2xl font-bold mt-1 tabular-nums">{MXN(cobrado)}</p>
        </div>
        {isAdmin && (
          <div className="rounded-xl border border-border bg-card p-4">
            <p className="text-[11px] uppercase tracking-widest text-muted-foreground">Sin vendedor</p>
            <p className="text-2xl font-bold mt-1 tabular-nums">
              {items.filter(s => !s.seller_user_id).length}
            </p>
          </div>
        )}
      </div>

      {isAdmin && sellers.length > 0 && (
        <select value={quien} onChange={e => setQuien(e.target.value)}
          className="px-3 py-2 text-sm rounded-lg bg-muted border border-border text-foreground outline-none">
          <option value="">Todo el equipo</option>
          {sellers.map(s => <option key={s.user_id} value={s.user_id}>{s.full_name || s.username}</option>)}
        </select>
      )}

      {err && <div className="rounded-lg border border-red-500/25 bg-red-500/10 text-red-400 px-3 py-2 text-sm">{err}</div>}
      {loading && <p className="text-muted-foreground text-sm flex items-center gap-2"><Loader size={14} className="animate-spin" />Cargando…</p>}

      {!loading && items.length === 0 && (
        <div className="rounded-xl border border-dashed border-border p-10 text-center">
          <ShoppingCart size={26} className="mx-auto text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">Sin ventas todavía.</p>
          {puedeRegistrar && (
            <p className="text-xs text-muted-foreground mt-1">Cuando cierres una venta por WhatsApp o teléfono, regístrala con «Registrar venta».</p>
          )}
        </div>
      )}

      {/* Celular (la vendedora vende desde ahí): tarjetas, sin scroll horizontal. */}
      {items.length > 0 && (
        <ul className="space-y-2 sm:hidden">
          {items.map(s => (
            <li key={s.id} className="rounded-xl border border-border bg-card p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{s.customer_name || '—'}</p>
                  <p className="truncate text-xs text-muted-foreground">{s.trip_name}</p>
                </div>
                <p className="shrink-0 text-sm font-semibold tabular-nums">{MXN(s.total)}</p>
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                <span className="font-mono text-muted-foreground">{s.confirmation_code}</span>
                <span className={`px-2 py-0.5 rounded-full border text-[11px] ${ESTADO[s.status] || ESTADO.expired}`}>{ESTADO_TXT[s.status] || s.status}</span>
                {isAdmin && (s.seller_user_id
                  ? <span className="text-muted-foreground">{nombreDe(s.seller_user_id)}</span>
                  : <span className="text-muted-foreground">sin vendedor</span>)}
              </div>
            </li>
          ))}
        </ul>
      )}

      {items.length > 0 && (
        <div className="hidden sm:block rounded-xl border border-border overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/60 text-muted-foreground">
              <tr className="text-left">
                <th className="px-3 py-2 font-medium">Código</th>
                <th className="px-3 py-2 font-medium">Cliente</th>
                <th className="px-3 py-2 font-medium">Viaje</th>
                <th className="px-3 py-2 font-medium text-right">Total</th>
                <th className="px-3 py-2 font-medium">Estado</th>
                <th className="px-3 py-2 font-medium">Vendedor</th>
              </tr>
            </thead>
            <tbody>
              {items.map(s => (
                <tr key={s.id} className="border-t border-border hover:bg-muted/30">
                  <td className="px-3 py-2 font-mono text-xs">{s.confirmation_code}</td>
                  <td className="px-3 py-2">
                    <div>{s.customer_name || '—'}</div>
                    <div className="text-xs text-muted-foreground">{s.customer_email}</div>
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">{s.trip_name}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold">{MXN(s.total)}</td>
                  <td className="px-3 py-2">
                    <span className={`text-[11px] px-2 py-0.5 rounded-full border ${ESTADO[s.status] || ESTADO.expired}`}>
                      {ESTADO_TXT[s.status] || s.status}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    {busy === s.id ? <Loader size={13} className="animate-spin" />
                      : s.seller_user_id ? (
                        <span className="text-xs">{nombreDe(s.seller_user_id)}</span>
                      ) : isAdmin && sellers.length > 0 ? (
                        <select
                          defaultValue=""
                          onChange={e => asignar(s, e.target.value)}
                          className="text-xs px-2 py-1 rounded bg-muted border border-border outline-none"
                        >
                          <option value="">Asignar…</option>
                          {sellers.map(v => (
                            <option key={v.user_id} value={v.user_id}>{v.full_name || v.username}</option>
                          ))}
                        </select>
                      ) : (
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <UserPlus size={11} />sin asignar
                        </span>
                      )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
